import React, { useCallback, useState } from "react";

import { Video } from "../../../Store/Reducers/VideoSlice";
import AdminVideoCard from "../AdminVideoCard";
import { reorderVideos, haveVideoOrdersChanged } from "../videoOrderUtils";

export interface RoleVideoGridProps {
  videos: Video[];
  isOrderEditMode: boolean;
  onVideosChange: (videos: Video[]) => void;
  onPreview: (video: Video) => void;
  onEdit?: (video: Video) => void;
  onDelete: (video: Video) => void;
  deleteTitle?: string | ((video: Video) => string);
  emptyMessage?: string;
  onStatusToggle: (video: Video, toActive: boolean) => void;
  getSharedRoleLabels?: (video: Video) => string[];
  getTagLabel?: (video: Video) => string | undefined;
  getIsActive?: (video: Video) => boolean;
  getStatusActionLabel?: (video: Video, isActive: boolean) => string;
  gridClassName?: string;
}

const RoleVideoGrid: React.FC<RoleVideoGridProps> = ({
  videos,
  isOrderEditMode,
  getSharedRoleLabels,
  getTagLabel,
  getIsActive,
  getStatusActionLabel,
  onVideosChange,
  onPreview,
  onEdit,
  onDelete,
  deleteTitle,
  emptyMessage = "No videos found for this role.",
  onStatusToggle,
  gridClassName = "admin-video-container__grid",
}) => {
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const handleDragStart = useCallback((videoId: string) => {
    setDraggingId(videoId);
  }, []);

  const handleDragOver = useCallback(
    (event: React.DragEvent, videoId: string) => {
      if (!isOrderEditMode || !draggingId || draggingId === videoId) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";

      if (dragOverId === videoId) return;
      setDragOverId(videoId);

      const reordered = reorderVideos(videos, draggingId, videoId);
      if (!haveVideoOrdersChanged(videos, reordered)) return;
      onVideosChange(reordered);
    },
    [dragOverId, draggingId, isOrderEditMode, onVideosChange, videos]
  );

  const handleDrop = useCallback(
    (_event: React.DragEvent, targetId: string) => {
      if (!isOrderEditMode || !draggingId) return;
      const reordered = reorderVideos(videos, draggingId, targetId);
      onVideosChange(reordered);
      setDraggingId(null);
      setDragOverId(null);
    },
    [draggingId, isOrderEditMode, onVideosChange, videos]
  );

  const handleDragEnd = useCallback(() => {
    setDraggingId(null);
    setDragOverId(null);
  }, []);

  if (videos.length === 0) {
    return (
      <p className="text-sm text-gray-500 py-6 text-center">{emptyMessage}</p>
    );
  }

  return (
    <div className={gridClassName}>
      {videos.map(video => {
        const isActive = getIsActive?.(video) ?? video.is_active ?? true;
        const resolvedDeleteTitle =
          typeof deleteTitle === "function" ? deleteTitle(video) : deleteTitle;
        return (
          <AdminVideoCard
            key={video.video_id}
            video={video}
            sharedRoleLabels={getSharedRoleLabels?.(video)}
            tagLabel={getTagLabel?.(video)}
            isActiveOverride={getIsActive ? isActive : undefined}
            statusActionLabel={getStatusActionLabel?.(video, isActive)}
            isOrderEditMode={isOrderEditMode}
            isDragging={draggingId === video.video_id}
            isDragOver={dragOverId === video.video_id}
            onPreview={onPreview}
            onEdit={onEdit}
            onDelete={onDelete}
            deleteTitle={resolvedDeleteTitle}
            onStatusToggle={onStatusToggle}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
          />
        );
      })}
    </div>
  );
};

export default RoleVideoGrid;
